import {
    attachClosestEdge,
    extractClosestEdge,
    type Edge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { DropIndicator } from "@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box";
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { useEffect, useRef, useState } from "react";

type RowState = { type: "idle" } | { type: "is-dragging" } | { type: "is-over"; closestEdge: Edge | null };

interface DraggableRowProps {
    index: number;
    handleDrop: (sourceIndex: number, destinationIndex: number) => void;
    children: any;
    [key: string]: any; // ...rest
}

function DraggableRow({ index, handleDrop, children, ...rest }: DraggableRowProps) {
    const ref = useRef<HTMLTableRowElement>(null);
    const [state, setState] = useState<RowState>({ type: "idle" });

    useEffect(() => {
        const element = ref.current;
        if (!element) return undefined;

        return draggable({
            element,
            getInitialData: () => ({ index }),
            onDragStart: () => setState({ type: "is-dragging" }),
            onDrop: () => setState({ type: "idle" }),
        });
    }, [index]);

    useEffect(() => {
        const element = ref.current;
        if (!element) return undefined;

        return dropTargetForElements({
            element,
            canDrop: ({ source }) => source.data.index !== index,
            getData: ({ input, element }) => {
                const data = { index };
                return attachClosestEdge(data, {
                    input,
                    element,
                    allowedEdges: ["top", "bottom"],
                });
            },
            onDragEnter: (args) => {
                setState({
                    type: "is-over",
                    closestEdge: extractClosestEdge(args.self.data),
                });
            },
            onDrag: (args) => {
                const closestEdge = extractClosestEdge(args.self.data);
                setState((current) => {
                    if (current.type !== "is-over") {
                        return current;
                    }
                    if (current.closestEdge === closestEdge) {
                        return current;
                    }
                    return {
                        type: "is-over",
                        closestEdge,
                    };
                });
            },
            onDragLeave: () => {
                setState({ type: "idle" });
            },
            onDrop: ({ source }) => {
                setState({ type: "idle" });
                const sourceIndex = source.data.index as number;
                if (sourceIndex !== index) {
                    handleDrop(sourceIndex, index);
                }
            },
        });
    }, [index, handleDrop]);

    return (
        <tr
            ref={ref}
            {...rest}
            style={{
                opacity: state.type === "is-dragging" ? 0.5 : 1,
                cursor: "grab",
                position: "relative",
            }}
        >
            {children}
            {state.type === "is-over" && state.closestEdge && <DropIndicator edge={state.closestEdge} gap="0px" />}
        </tr>
    );
}

export default DraggableRow;
